/* eslint-disable no-console */
import client from 'braintree-web/client';
import filter from 'lodash/filter';
import upperCase from 'lodash/upperCase';
import cookies from 'react-cookies';
import axios, {
  logToSplunk,
} from '../components/_generic/axiosSplunk/axiosSplunk';
import { ADDON_EXTENSION } from '../config/constants/addon-types';
import { PAYMENT_TYPES, PAYMENT_METHODS } from '../config/constants/payment';
import COOKIES from '../config/cookies/cookies';
import { URLPAYBTADDON } from '../config/setup/setup';
import { trackCall, trackEvent, trackPurchase } from './analytics';
import ErrorTrackingService from './errorTrackingService';
import httpCommonHeaders from './httpCommonHeaders';
import {
  paymentErrorActionHandler,
  paymentErrorMessageHandler,
} from './paymentErrorHandler';
import paymentPayload from './paymentPayload';
import WalletService from './walletService';

const PAY_TIMEOUT = 40_000;

const braintreeService = {
  addons: null,
  allowSubscription: null,
  clientId: null,
  expressBuy: false,
  id: null,
  isPostCheckout: false,
  isRedeemPostage: false,
  marketingWalletCredit: 0,
  nonce: null,
  paymentComplete: null,
  paymentError: null,
  paymentMethod: null,
  paymentType: null,
  postageDiscount: 0,
  postageOnCheckoutRule: 0,
  postageOnRedemption: false,
  productOrderId: null,
  products: [],
  promoCode: null,
  providerError: null,
  refundableWalletCredit: 0,
  token: null,
  totalQuantity: 0,
  vipOptIn: false,
  vipOptInType: '',
  vipSubscriptionDetails: {},
  walletUsed: false,
};

const fireSuccessfulTracking = (credit) => {
  if (braintreeService.paymentType === PAYMENT_TYPES.card) {
    trackEvent('card_successful');
  }
  if (braintreeService.paymentType === PAYMENT_TYPES.paypal) {
    trackEvent(credit ? 'paypal_credit_successful' : 'paypal_successful');
  }
  if (braintreeService.paymentType === PAYMENT_TYPES.google) {
    trackEvent('googlepay_successful');
  }
  if (braintreeService.paymentType === PAYMENT_TYPES.apple) {
    trackEvent('applepay_successful');
  }
  if (
    braintreeService.isPostCheckout &&
    braintreeService.addons[0].addonType === ADDON_EXTENSION
  ) {
    trackEvent('ancilliary_payment_complete');
  }
  const paymentSource = localStorage.getItem('expressPaymentSource');
  if (paymentSource) {
    trackCall(`sticky_checkout_${paymentSource}_completed`);
    localStorage.removeItem('expressPaymentSource');
  }
};

const fireFailureTracking = (credit) => {
  if (braintreeService.paymentType === PAYMENT_TYPES.card) {
    trackEvent('card_failed');
  }
  if (braintreeService.paymentType === PAYMENT_TYPES.paypal) {
    trackEvent(credit ? 'paypal_credit_failed' : 'paypal_failed');
  }
  if (braintreeService.paymentType === PAYMENT_TYPES.google) {
    trackEvent('googlepay_failed');
  }
  if (braintreeService.paymentType === PAYMENT_TYPES.apple) {
    trackEvent('applepay_failed');
  }
};

braintreeService.getGoogleLoggedOutClientInstance = async (
  expressBuyAuthToken,
) => {
  try {
    const tokenResponse = await braintreeService.getClientTokenWithExpressBuyToken(
      expressBuyAuthToken,
    );

    if (tokenResponse) {
      braintreeService.token = tokenResponse.data.clientToken;
      braintreeService.clientId = tokenResponse.data.clientId;

      return client.create({ authorization: tokenResponse.data.clientToken });
    }

    return null;
  } catch (error) {
    return error;
  }
};

braintreeService.getClientInstance = async (
  isLoggedOutUser,
  isFastlane = false,
  authToken = null,
) => {
  try {
    let tokenResponse;
    if (isLoggedOutUser) {
      tokenResponse = await braintreeService.getLoggedOutClientToken();
    } else if (isFastlane && authToken) {
      tokenResponse = await braintreeService.getClientTokenWithExpressBuyToken(
        authToken,
      );
    } else {
      tokenResponse = await braintreeService.getClientToken();
    }

    if (tokenResponse) {
      braintreeService.token = tokenResponse.data.clientToken;
      braintreeService.clientId = tokenResponse.data.clientId;

      return client.create({ authorization: tokenResponse.data.clientToken });
    }

    return null;
  } catch (error) {
    return error;
  }
};

braintreeService.getFastlaneToken = () => {
  return axios(`${process.env.NEXT_PUBLIC_PAYMENT_URL}/paypal-fastlane-token`, {
    cache: false,
    headers: httpCommonHeaders(),
    method: 'GET',
    withCredentials: true,
  });
};

braintreeService.getLoggedOutClientToken = () => {
  return axios(`${process.env.NEXT_PUBLIC_PAYMENT_URL}/ghost-token`, {
    cache: false,
    headers: httpCommonHeaders(),
    method: 'GET',
    withCredentials: true,
  });
};

braintreeService.getClientToken = () => {
  return axios(`${process.env.NEXT_PUBLIC_PAYMENT_URL}/token`, {
    cache: false,
    headers: httpCommonHeaders(),
    method: 'GET',
    withCredentials: true,
  });
};

braintreeService.getClientTokenWithExpressBuyToken = (expressBuyAuthToken) => {
  return axios(`${process.env.NEXT_PUBLIC_PAYMENT_URL}/token`, {
    cache: false,
    headers: {
      ...httpCommonHeaders(),
      'x-expressbuy-auth': expressBuyAuthToken,
    },
    method: 'GET',
    withCredentials: true,
  });
};

braintreeService.getNonce = (token) => {
  return axios(`${process.env.NEXT_PUBLIC_PAYMENT_URL}/nonce/${token}`, {
    cache: false,
    headers: httpCommonHeaders(),
    method: 'GET',
  });
};

const getCards = (braintreeId) => {
  return axios(
    `${process.env.NEXT_PUBLIC_PAYMENT_URL}/customer/${braintreeId}`,
    {
      cache: false,
      headers: httpCommonHeaders(),
      method: 'GET',
    },
  );
};

braintreeService.getVaultedCards = async (paypal = false) => {
  try {
    const expressAuthToken = localStorage.getItem(COOKIES.expressAuthToken);
    const isFastlane = cookies.load(COOKIES.isFastlane) === 'true';
    if (isFastlane && expressAuthToken) {
      return null;
    }
    let tokenResponse;
    if (expressAuthToken) {
      tokenResponse = await braintreeService.getClientTokenWithExpressBuyToken(
        expressAuthToken,
      );
    } else {
      tokenResponse = await braintreeService.getClientToken();
    }
    // const tokenResponse = await braintreeService.getClientToken();
    const clientId = tokenResponse?.data?.clientId || null;

    if (clientId) {
      const cards = await getCards(clientId);
      if (paypal) {
        return cards.data.customer.payPalAccounts;
      }
      if (cards) {
        return Object.values(
          cards?.data?.customer?.creditCards.reduce(
            (accumulator, current) =>
              Object.assign({}, accumulator, {
                [current.maskedNumber]: current,
              }),
            {},
          ),
        ).filter((card) => !card.expired);
      }
    }

    return null;
  } catch (error) {
    ErrorTrackingService.logError(error);
    console.log('error'.error);

    return null;
  }
};

const checkPromoCode = ({ orderSummary }) => {
  if (
    orderSummary?.promoCode?.applicable &&
    orderSummary?.promoCode?.value &&
    !braintreeService?.promoCode
  ) {
    braintreeService.promoCode = orderSummary.promoCode.value;
    ErrorTrackingService.logError({
      error: 'BraintreeService Promo Code Error',
      message: 'BraintreeService is not applying promo codes correctly',
    });
  }
};

const submitPurchase = async ({
  credit,
  expressCheckout,
  expressBuyAuthToken,
  expressDealCheckout,
  fastlaneFlow = false,
}) => {
  try {
    let order;
    if (expressBuyAuthToken) {
      order = await braintreeService.makePayment({
        expressBuyAuthToken,
        expressCheckout,
        expressDealCheckout,
        fastlaneFlow,
      });
    } else if (fastlaneFlow) {
      order = await braintreeService.makePayment({
        expressCheckout,
        expressDealCheckout,
        fastlaneFlow,
      });
    } else if (expressCheckout) {
      order = await braintreeService.makePayment({ expressCheckout });
    } else {
      order = await braintreeService.makePayment({ expressDealCheckout });
    }
    fireSuccessfulTracking(credit);

    // Total number of individual units bought
    order.data.quantity = braintreeService.totalQuantity;

    if (braintreeService.paymentType === PAYMENT_TYPES.card)
      order.data.cardType = PAYMENT_METHODS.card;
    if (braintreeService.paymentType === PAYMENT_TYPES.paypal) {
      order.data.cardType = credit
        ? PAYMENT_METHODS.payPalCredit
        : PAYMENT_METHODS.paypal;
    }
    if (braintreeService.paymentType === PAYMENT_TYPES.google)
      order.data.cardType = PAYMENT_METHODS.googlePay;
    if (braintreeService.paymentType === PAYMENT_TYPES.apple)
      order.data.cardType = PAYMENT_METHODS.applePay;
    order.data.currency = braintreeService.currency;
    order.data.walletAmountUsed = braintreeService.walletUsed
      ? braintreeService.walletAmount
      : 0;

    trackPurchase(
      braintreeService.products,
      order,
      false,
      braintreeService.isPostCheckout
        ? braintreeService?.addons[0]?.addonType
        : null,
      braintreeService.isPostCheckout,
      braintreeService.vipOptInType,
    );
    braintreeService.paymentComplete(order);

    return console.info('Payment Successful');
  } catch (error) {
    ErrorTrackingService.logError(error);
    braintreeService.paymentError(error);
    fireFailureTracking(credit);
    if (error?.response?.data?.reasonCode) {
      braintreeService.providerError({
        action: paymentErrorActionHandler(error.response.data.reasonCode),
        message: paymentErrorMessageHandler(error.response.data.reasonCode),
      });
    }

    return console.info('Payment Failure');
  }
};

braintreeService.preparePayment = async ({
  basketProducts,
  credit,
  dataCollectorInstance,
  isWalletApplied,
  orderSummary,
  paymentMethod,
  tokenizationPayload,
  expressBuyAuthToken = null,
  expressCheckout,
  expressDealCheckout,
  paymentMethodNonce = null,
  fastlaneFlow = false,
}) => {
  try {
    if (!expressBuyAuthToken && !paymentMethodNonce) {
      const response = await WalletService.getWallet();
      const userWallets = filter(response.data, (wallet) => {
        return wallet.currency === upperCase(orderSummary.currency);
      });
      braintreeService.walletAmount = userWallets[0].balance;
    }
    const resolvedTokenizationPayload = await tokenizationPayload;
    braintreeService.paymentMethod = paymentMethod;
    braintreeService.walletUsed = isWalletApplied;
    braintreeService.postageDiscount = orderSummary.totalPostageDiscount;
    braintreeService.postageOnRedemption = orderSummary.postageOnRedemption;
    braintreeService.postageOnCheckoutRule = orderSummary.postageOnCheckoutRule;
    braintreeService.products = basketProducts;
    braintreeService.currency = upperCase(orderSummary.currency);
    braintreeService.token = resolvedTokenizationPayload?.cardToken
      ? resolvedTokenizationPayload?.cardToken
      : null;
    braintreeService.token = expressBuyAuthToken;
    if (paymentMethodNonce) braintreeService.nonce = paymentMethodNonce;
    else braintreeService.nonce = resolvedTokenizationPayload?.nonce;
    braintreeService.isRedeemPostage = orderSummary.postageOnRedemption;
    if (dataCollectorInstance) {
      braintreeService.deviceData = dataCollectorInstance.deviceData;
    }
    checkPromoCode({ orderSummary });
    if (expressBuyAuthToken) {
      submitPurchase({
        credit,
        expressBuyAuthToken,
        expressCheckout,
        expressDealCheckout,
        fastlaneFlow,
      });
    }
    // else if (fastlaneFlow) {
    //   submitPurchase({
    //     credit,
    //     expressCheckout,
    //     expressDealCheckout,
    //     fastlaneFlow,
    //   });
    // }
    else {
      submitPurchase({
        credit,
        expressCheckout,
        expressDealCheckout,
        fastlaneFlow,
      });
    }
  } catch (error) {
    ErrorTrackingService.logError(error);
  }
};

braintreeService.makePayment = async (args = {}) => {
  const {
    expressCheckout = false,
    expressBuyAuthToken = false,
    expressDealCheckout = false,
    fastlaneFlow = false,
  } = args;
  const payload = await paymentPayload({
    expressCheckout,
    expressDealCheckout,
    fastlaneFlow,
    provider: 'BT',
    service: braintreeService,
  });
  let url = `${process.env.NEXT_PUBLIC_PAYMENT_URL}/pay${
    braintreeService.promoCode ? `?promoCode=${braintreeService.promoCode}` : ''
  }`;

  if (braintreeService.isPostCheckout) {
    url = `${URLPAYBTADDON}/${braintreeService.productOrderId}`;
  }
  if (expressBuyAuthToken) {
    return axios(
      url,
      {
        cache: false,
        data: payload,
        headers: {
          ...httpCommonHeaders(),
          expressPurchaseSource: fastlaneFlow ? 'payPalFastlane' : 'checkout',
          'x-expressbuy-auth': expressBuyAuthToken,
        },
        method: 'POST',
        timeout: PAY_TIMEOUT,
        url,
        withCredentials: true,
      },
      PAY_TIMEOUT,
    );
  } else if (expressCheckout || fastlaneFlow) {
    return axios(
      url,
      {
        cache: false,
        data: payload,
        headers: {
          ...httpCommonHeaders(),
          expressPurchaseSource: fastlaneFlow ? 'payPalFastlane' : 'checkout',
        },
        method: 'POST',
        timeout: PAY_TIMEOUT,
        url,
        withCredentials: true,
      },
      PAY_TIMEOUT,
    );
  } else {
    return axios(
      url,
      {
        cache: false,
        data: payload,
        headers: httpCommonHeaders(),
        method: 'POST',
        timeout: PAY_TIMEOUT,
        url,
        withCredentials: true,
      },
      PAY_TIMEOUT,
    );
  }
};

braintreeService.reset = () => {
  braintreeService.addons = null;
  braintreeService.allowSubscription = null;
  braintreeService.clientId = null;
  braintreeService.expressBuy = false;
  braintreeService.id = null;
  braintreeService.isPostCheckout = false;
  braintreeService.isRedeemPostage = false;
  braintreeService.marketingWalletCredit = 0;
  braintreeService.nonce = null;
  braintreeService.paymentComplete = null;
  braintreeService.paymentError = null;
  braintreeService.paymentMethod = null;
  braintreeService.paymentType = null;
  braintreeService.postageDiscount = 0;
  braintreeService.postageOnCheckoutRule = 0;
  braintreeService.postageOnRedemption = false;
  braintreeService.productOrderId = null;
  braintreeService.products = [];
  braintreeService.promoCode = null;
  braintreeService.providerError = null;
  braintreeService.refundableWalletCredit = 0;
  braintreeService.token = null;
  braintreeService.totalQuantity = 0;
  braintreeService.vipOptIn = false;
  braintreeService.vipOptInType = '';
  braintreeService.vipSubscriptionDetails = {};
  braintreeService.walletUsed = false;
};

export default braintreeService;
